import { useSelector } from '@xstate/react';
import { useEffect } from 'react';
import type { ActorRefFrom, SnapshotFrom } from 'xstate';
import { Button, LoadingSpinner } from '~/components';
import {
  EmployerInfoForm,
  type EmployerInfoValues,
  employerInfoFormToApi,
} from '~/features/employer/components';
import { useEmployerInfoRef } from '~/features/market/marketSelectors';
import { employerInfoMachine } from './employerInfo.machine';

type EmployerInfoSnapshot = SnapshotFrom<typeof employerInfoMachine>;

const selectForm = (state: EmployerInfoSnapshot) => state.context.form;
const selectLoading = (state: EmployerInfoSnapshot) => state.context.loading;
const selectSaving = (state: EmployerInfoSnapshot) => state.context.saving;
const selectHasError = (state: EmployerInfoSnapshot) => state.matches('$_ERROR');
const selectIsIdle = (state: EmployerInfoSnapshot) => state.matches('$_IDLE');

export const EmployerInfoPanel = () => {
  const { employerInfoRef } = useEmployerInfoRef();

  if (!employerInfoRef) {
    return null;
  }

  return <EmployerInfoPanelInner employerInfoRef={employerInfoRef} />;
};

const EmployerInfoPanelInner = ({
  employerInfoRef,
}: {
  employerInfoRef: ActorRefFrom<typeof employerInfoMachine>;
}) => {
  const form = useSelector(employerInfoRef, selectForm);
  const loading = useSelector(employerInfoRef, selectLoading);
  const saving = useSelector(employerInfoRef, selectSaving);
  const hasError = useSelector(employerInfoRef, selectHasError);
  const isIdle = useSelector(employerInfoRef, selectIsIdle);

  useEffect(() => {
    if (isIdle) {
      employerInfoRef.send({ type: 'ON_LOAD' });
    }
  }, [employerInfoRef, isIdle]);

  const handleSubmit = (data: EmployerInfoValues) => {
    employerInfoRef.send({
      type: 'ON_SUBMIT',
      payload: { data: employerInfoFormToApi(data) },
    });
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (hasError) {
    return (
      <div className="flex items-center justify-between gap-4">
        <p className="body-text text-grey-200">Unable to load employer info.</p>
        <Button
          type="button"
          label="Retry"
          onClick={() => employerInfoRef.send({ type: 'ON_RETRY' })}
        />
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <EmployerInfoForm
        defaultValues={form}
        onSubmit={handleSubmit}
        submitLabel="Save Changes"
        loading={saving}
      />
    </div>
  );
};
